import { cn } from '#app/utils/misc.tsx'
import { OyAvatar } from './oy-avatar.tsx'
import { OyProgressBar } from './oy-card.tsx'
import { OyPill } from './oy-pill.tsx'

/**
 * Stal-domain patterns (plan §1.6): the day row with the duty avatar and
 * the amber "wie?" pill, the horse card, the upcoming-day card, the lesson
 * row with its state pill, the week strip coloured by state, the tally bar
 * with the target tick, the chore row with the "wanneer weer" bar, the
 * person row and the closed door. Presentational; strings come in.
 */

export function StalSectionLabel({
	children,
	meta,
	end,
	id,
}: {
	children: React.ReactNode
	meta?: React.ReactNode
	end?: React.ReactNode
	id?: string
}) {
	return (
		<div className="st-sectionlabel">
			<h2 id={id}>{children}</h2>
			{meta ? <span className="meta">{meta}</span> : null}
			{end ? <span className="end">{end}</span> : null}
		</div>
	)
}

export type StalChip = {
	label: string
	tone: 'done' | 'accent' | 'warm' | 'muted'
}

export function StalDayRow({
	dow,
	num,
	today,
	dutyName,
	dutyHue,
	chips,
	onOpen,
	label,
}: {
	dow: string
	num: string
	today?: boolean
	dutyName: string | null
	dutyHue: string | null
	chips: StalChip[]
	onOpen: () => void
	/** accessible name for the row button */
	label: string
}) {
	return (
		<button
			type="button"
			className={cn('oy-dayrow', today && 'today')}
			onClick={onOpen}
			aria-label={label}
		>
			<span className="oy-dayrow-date">
				{dow}
				<b>{num}</b>
			</span>
			<span className="oy-dayrow-body">
				<span className="oy-dayrow-who">
					{dutyName ? (
						<OyAvatar name={dutyName} hue={dutyHue ?? undefined} />
					) : (
						<span
							className="oy-avatar"
							style={{ background: 'var(--oy-warm)' }}
							aria-hidden="true"
						>
							?
						</span>
					)}
					<span className="name">{dutyName ?? 'nog niemand'}</span>
					<span className="meta">mestdienst</span>
					{!dutyName ? <OyPill tone="warm">wie?</OyPill> : null}
				</span>
				{chips.length ? (
					<span className="oy-dayrow-pills">
						{chips.map((c, i) => (
							<OyPill key={i} tone={c.tone}>
								{c.label}
							</OyPill>
						))}
					</span>
				) : null}
			</span>
			<span className="chev" aria-hidden="true">
				›
			</span>
		</button>
	)
}

export function StalUpcomingCard({
	dow,
	dutyName,
	dutyHue,
	sub,
	onOpen,
}: {
	dow: string
	dutyName: string | null
	dutyHue: string | null
	sub: string
	onOpen: () => void
}) {
	return (
		<button
			type="button"
			className={cn('st-daycard', !dutyName && 'open')}
			onClick={onOpen}
			aria-label={`${dow}: ${dutyName ?? 'nog niemand'} mestdienst, ${sub}`}
		>
			<div className="dow">{dow}</div>
			<div className="who">
				{dutyName ? (
					<OyAvatar name={dutyName} hue={dutyHue ?? undefined} size={24} />
				) : (
					<span
						className="oy-avatar size-24"
						style={{ background: 'var(--oy-warm)' }}
						aria-hidden="true"
					>
						?
					</span>
				)}
				<span>{dutyName ?? 'wie?'}</span>
			</div>
			<div className="sub">{sub}</div>
		</button>
	)
}

export function StalHorseCard({
	name,
	status,
	rest,
	rider,
	flag,
	onOpen,
}: {
	name: string
	status: string
	/** the regel tint (rust / ziek / dagje vrij) instead of the paard tint */
	rest?: boolean
	rider: string
	flag?: string | null
	onOpen: () => void
}) {
	return (
		<button
			type="button"
			className={cn('st-horse', rest && 'rest')}
			onClick={onOpen}
		>
			<div className="head">
				<span style={{ fontSize: 22 }} aria-hidden="true">
					🐴
				</span>
				<span className="name">{name}</span>
			</div>
			<div className="status">{status}</div>
			<div className="rider">{rider}</div>
			{flag ? <div className="flag">{flag}</div> : null}
		</button>
	)
}

export function StalLessonRow({
	dow,
	num,
	time,
	what,
	instructorName,
	instructorHue,
	state,
	tone,
	onOpen,
}: {
	dow: string
	num: string
	time: string
	what: string
	instructorName: string
	instructorHue: string
	state: string
	tone: 'done' | 'warm' | 'muted'
	onOpen: () => void
}) {
	return (
		<button type="button" className="st-lesson" onClick={onOpen}>
			<span className="date">
				{dow}
				<b>{num}</b>
			</span>
			<span className="body">
				<span className="line">
					<span className="time">{time}</span>
					<span className="what">{what}</span>
				</span>
				<span className="pills">
					<span className="instr">
						<i style={{ background: instructorHue }} aria-hidden="true" />
						{instructorName}
					</span>
					<OyPill tone={tone}>{state}</OyPill>
				</span>
			</span>
			<span className="chev" aria-hidden="true">
				›
			</span>
		</button>
	)
}

export function StalWeekStrip({
	days,
}: {
	days: Array<{
		dow: string
		label: string
		tone: 'done' | 'warm' | 'muted' | null
	}>
}) {
	return (
		<div className="st-weekstrip" role="list" aria-label="Deze week">
			{days.map((d) => (
				<span key={d.dow} role="listitem">
					<span className="dow">{d.dow}</span>
					<span className={cn('cell', d.tone)}>{d.label}</span>
				</span>
			))}
		</div>
	)
}

export function StalTallyRow({
	name,
	hue,
	count,
	pct,
	target,
}: {
	name: string
	hue: string
	count: string
	pct: number
	target?: number | null
}) {
	return (
		<div className="oy-tallyrow">
			<OyAvatar name={name} hue={hue} />
			<span className="name">{name}</span>
			<OyProgressBar
				pct={pct}
				tone={hue}
				target={target ?? undefined}
				tally
				label={`${name}: ${count}`}
			/>
			<span className="n">{count}</span>
		</div>
	)
}

export function StalChoreRow({
	icon,
	title,
	meta,
	pct,
	due,
	tile,
	onDone,
}: {
	icon: string
	title: string
	meta: string
	pct: number | null
	/** the bar turns warm once the interval has elapsed */
	due: boolean
	tile?: 'paard' | 'regel' | 'chip'
	onDone: () => void
}) {
	return (
		<div className="st-chore">
			<span
				className="oy-tile"
				aria-hidden="true"
				style={{
					background:
						tile === 'paard'
							? 'var(--oy-cat-paard)'
							: tile === 'regel'
								? 'var(--oy-cat-regel)'
								: undefined,
				}}
			>
				{icon}
			</span>
			<span className="body">
				<span className="text">{title}</span>
				<span className="meta">{meta}</span>
				{pct != null ? (
					<OyProgressBar pct={pct} tone={due ? 'warm' : 'done'} />
				) : null}
			</span>
			<button
				type="button"
				className="oy-btn oy-btn-secondary"
				onClick={onDone}
			>
				Gedaan
			</button>
		</div>
	)
}

export function StalPersonRow({
	name,
	hue,
	role,
	away,
	onOpen,
}: {
	name: string
	hue: string
	role: string
	away?: string | null
	onOpen: () => void
}) {
	return (
		<button type="button" className="st-person" onClick={onOpen}>
			<OyAvatar name={name} hue={hue} size={36} />
			<span className="body">
				<span className="name">{name}</span>
				<span className="role">{role}</span>
			</span>
			{away ? <OyPill tone="warm">{away}</OyPill> : null}
		</button>
	)
}

/** The friendly closed door (ADR-013): leaks nothing, names the next step. */
export function StalClosedDoor({
	title,
	children,
}: {
	title: string
	children: React.ReactNode
}) {
	return (
		<div className="st-closed">
			<span className="oy-stal-brand" aria-hidden="true">
				🐴
			</span>
			<h2>{title}</h2>
			<p>{children}</p>
		</div>
	)
}
